<template>
	<v-card>
		<v-card-title class="dialog-header sgnl-subtitle-medium">
			Sign Up for Access
			<v-spacer />
			<v-btn
				icon
				variant="text"
				size="small"
				@click="$emit('close-dialog')"
			>
				<Close16 />
			</v-btn>
		</v-card-title>
		<v-divider />
		<v-card-text class="dialog-body">
			<v-form
				v-model="formIsValid"
				@submit.prevent="onSubmit"
				ref="interestForm"
				action="/"
				class="stack"
			>
				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.plan }}</div>
					<v-select
						v-model="formValues.plan"
						required
						disabled
						:rules="formRules.plan"
						:items="planSelectItems"
						hide-details="auto"
					/>
				</div>

				<v-divider />

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.fullname }}</div>
					<v-text-field
						v-model="formValues.fullname"
						name="name"
						autocomplete="name"
						:rules="formRules.fullname"
						validate-on-blur
						required
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.email }}</div>
					<v-text-field
						v-model="formValues.email"
						type="email"
						name="email"
						autocomplete="email"
						:rules="formRules.email"
						required
						validate-on-blur
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.affiliation }}</div>
					<v-text-field
						v-model="formValues.affiliation"
						:rules="formRules.affiliation"
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.howDidYouHearAboutThis }}</div>
					<v-textarea
						v-model="formValues.howDidYouHearAboutThis"
						rows="4"
						auto-grow
						:rules="formRules.howDidYouHearAboutThis"
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.applicationDomain }}</div>
					<v-textarea
						v-model="formValues.applicationDomain"
						rows="4"
						auto-grow
						:rules="formRules.applicationDomain"
						hint="We use this information in prioritizing your request for remote access to the Signaloid computing platform."
						persistent-hint
						hide-details="auto"
					/>
				</div>

				<v-divider />

				<div
					v-for="(value, key) of labelValues.survey"
					:key="value"
					class="sgnl-flex sgnl-flex-column sgnl-gap-8"
				>
					<span class="sgnl-paragraph-m-medium">{{ value }}</span>
					<v-radio-group
						v-model="formValues.survey[key]"
						density="compact"
						hint="We use this information in prioritizing your request for remote access to the Signaloid computing platform."
						persistent-hint
						:rules="formRules.surveyRules"
						:row="!$vuetify.display.mobile"
					>
						<v-radio
							label="Strongly Disagree"
							value="strongly-disagree"
						/>
						<v-radio
							label="Disagree"
							value="disagree"
						/>
						<v-radio
							label="Neutral"
							value="neutral"
						/>
						<v-radio
							label="Agree"
							value="agree"
						/>
						<v-radio
							label="Strongly Agree"
							value="strongly-agree"
						/>
					</v-radio-group>
				</div>
			</v-form>
		</v-card-text>
		<v-divider />
		<v-card-actions class="dialog-footer">
			<v-btn
				variant="outlined"
				@click="$emit('close-dialog')"
				>Cancel</v-btn
			>
			<v-btn
				variant="flat"
				color="primary"
				:loading="submitButtonLoading"
				:disabled="!formIsValid"
				@click="onSubmit"
				>Submit</v-btn
			>
		</v-card-actions>
	</v-card>
</template>

<script>
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import { fetchAuthSession } from "aws-amplify/auth";
import { joinWaitlist } from "@/js/signaloidClient";
import { monitoringCaptureError } from "@/plugins/monitoring";

export default defineComponent({
	name: "InterestForm",
	components: { Close16 },
	emits: ["form-submitted", "form-submission-failed", "close-dialog"],
	data: () => ({
		formIsValid: false,
		submitButtonLoading: false,
		formPostSuccess: false,
		formValues: {
			plan: "",
			fullname: "",
			email: "",
			affiliation: "",
			howDidYouHearAboutThis: "",
			applicationDomain: "",
			survey: {
				evaluatingCompetingSolutions: "",
			},
		},
		formRules: {
			plan: [(v) => v.length > 0 || "This field is required."],
			fullname: [(v) => v.length > 0 || "This field is required."],
			email: [
				(v) => v.length > 0 || "This field is required.",
				(v) => /.+@.+\..+/.test(v) || "Please enter a valid email.",
			],
			affiliation: [(v) => v.length > 0 || "This field is required."],
			howDidYouHearAboutThis: [(v) => v.length > 0 || "This field is required."],
			applicationDomain: [(v) => v.length > 0 || "This field is required."],
			surveyRules: [(v) => v.length > 0 || "This field is required."],
		},
		labelValues: {
			plan: "Plan you are interested in",
			fullname: "Full Name",
			email: "Email",
			affiliation: "Company/Institution Name",
			howDidYouHearAboutThis: "How did you hear about this?",
			applicationDomain: "What is your application domain?",
			survey: {
				evaluatingCompetingSolutions: "I am evaluating competing solutions",
			},
		},
		planSelectItems: ["Developer", "Developer+API", "Enterprise", "Enterprise S", "Enterprise M", "Enterprise L"],
	}),
	props: {
		passedFullname: { type: String, default: null },
		passedEmail: { type: String, default: null },
		passedPlan: { type: String, default: null },
	},
	watch: {
		passedPlan: function () {
			/*
			 *  `passedPlan` is the only prop that may change (for the same
			 *  Authentication Session).
			 */
			this.registerPassedProps();
		},
	},
	mounted() {
		this.registerPassedProps();
	},
	methods: {
		registerPassedProps() {
			this.formValues.fullname = this.passedFullname || "";
			this.formValues.email = this.passedEmail || "";
			if (this.planSelectItems.includes(this.passedPlan)) {
				this.formValues.plan = this.passedPlan || "";
			}
		},
		async onSubmit() {
			this.formPostSuccess = false;
			if (!this.$refs.interestForm.validate()) {
				// console.log("form invalid");
				return;
			}
			// console.log("valid");

			const message = {};
			message["interest-form"] = this.formValues;
			message["form-labels"] = this.labelValues;

			const planSelectItems = this.planSelectItems;
			message["form-choices"] = {
				planSelectItems,
			};

			/*
			 *  Build a pretty version of the message.
			 */
			let messagePretty = "";
			messagePretty += "A user has completed the Contact Us form.\n\n";

			for (const key in this.labelValues) {
				if (key === "survey") {
					messagePretty += "\n# Survey\n";
					for (const surveyKey in this.labelValues[key]) {
						if (Object.hasOwnProperty.call(this.labelValues[key], surveyKey)) {
							messagePretty += `${this.labelValues[key][surveyKey]} : ${this.formValues[key][surveyKey]}\n`;
						}
					}
				} else if (Object.hasOwnProperty.call(this.labelValues, key)) {
					messagePretty += `${this.labelValues[key]} : ${this.formValues[key]}\n`;
					if (key === "plan") {
						messagePretty += "\n# Personal Details\n";
					}
				}
			}

			try {
				const session = await fetchAuthSession();
				const idToken = session.tokens?.idToken;
				messagePretty += "\n# User Metadata\n";
				messagePretty += `CognitoUser email: ${idToken?.payload?.["cognito:username"] ?? ""}\n`;
				messagePretty += `CognitoUser sub: ${idToken?.payload?.["sub"] ?? ""}\n`;
				messagePretty += `CognitoUser name: ${idToken?.payload?.["name"] ?? ""}\n`;
				messagePretty += `CognitoUser groups: ${idToken?.payload?.["cognito:groups"] ?? ""}\n`;
			} catch (error) {
				monitoringCaptureError(error, "Submit interest form");
			}

			try {
				this.submitButtonLoading = true;
				await joinWaitlist(messagePretty);
				this.$refs.interestForm.reset();
				// @ts-ignore
				this.$posthog?.capture("submitted_interest_form");

				// Emit event to close dialog and show snackbar
				this.$emit("form-submitted");
			} catch (error) {
				monitoringCaptureError(error, "Submit interest form");
				// Emit failure event
				this.$emit("form-submission-failed");
			} finally {
				this.submitButtonLoading = false;
			}
		},
	},
});
</script>
